<template>
  <b-card class="border rounded-xl border-gray-200 mb-3">
    <!-- Header -->
    <div class="flex items-center justify-between pb-1 mb-2 border-b">
      <div>
        <h5 class="text-base font-semibold text-[#333333] mb-0.5">
          Setting Waktu CS AI
        </h5>
        <p class="text-xs md:text-sm text-[#828282] mb-0">
          Atur subsidi ongkir dengan lebih baik dan efisien.
        </p>
      </div>
      <b-form-checkbox
        id="toggle-cs-time"
        v-model="setCsTime"
        switch
        class="custom-cs-switch mb-0"
      />
    </div>

    <!-- Body -->
    <div
      v-show="setCsTime"
      class="mt-2"
    >
      <div class="text-sm font-medium text-[#333333] mb-1">
        Atur Hari CS<span class="text-danger">*</span>
      </div>

      <!-- Select All -->
      <b-form-checkbox
        :checked="isAllDaysSelected"
        class="custom-cs-checkbox mb-2 text-sm text-[#333333]"
        @change="toggleSelectAllDays"
      >
        Pilih semua ({{ selectedDays.length }}/{{ dayOptions.length }})
      </b-form-checkbox>

      <!-- Day Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div
          v-for="day in dayOptions"
          :key="day.id"
          class="border border-gray-200 rounded-lg p-2 bg-white transition-all hover:border-gray-300"
        >
          <!-- Header of Day Card -->
          <div class="flex items-center justify-between">
            <!-- Left: Checkbox with Day Name -->
            <b-form-checkbox
              :id="`checkbox-day-${day.id}`"
              :checked="isDaySelected(day)"
              class="custom-cs-checkbox mb-0"
              @change="val => toggleDaySelection(day, val)"
            >
              <span class="text-sm font-medium text-[#1E293B]">{{ day.name }}</span>
            </b-form-checkbox>

            <!-- Right: 24 Jam Toggle Badge / Button -->
            <button
              v-if="isDaySelected(day)"
              type="button"
              class="text-xs px-2 py-0.5 rounded border font-medium cursor-pointer focus:outline-none transition-colors"
              :class="getDayConfig(day).fullDay ? 'text-[#F97316] bg-[#FFF7ED] border-[#FFEDD5] hover:bg-orange-100' : 'text-gray-600 bg-gray-50 border-gray-200 hover:bg-gray-100'"
              @click="toggleFullDay(day)"
            >
              {{ getDayConfig(day).fullDay ? '24 Jam' : 'Atur Jam' }}
            </button>
            <span
              v-else
              class="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded border border-gray-200 font-medium opacity-60"
            >
              24 Jam
            </span>
          </div>

          <!-- Custom Time Range (Shown when day is selected and NOT 24 jam) -->
          <div
            v-if="isDaySelected(day) && !getDayConfig(day).fullDay"
            class="mt-2 pt-2 border-t border-gray-100"
          >
            <div class="grid grid-cols-2 gap-2">
              <!-- Jam Mulai -->
              <div>
                <label class="text-xs text-[#828282] mb-0.5 block">Jam Mulai</label>
                <flat-pickr
                  :value="getDayConfig(day).start || '08:00'"
                  :config="configDate"
                  class="form-control form-control-sm text-center bg-white"
                  @input="val => updateDayTime(day, 'start', val)"
                />
              </div>

              <!-- Jam Akhir -->
              <div>
                <label class="text-xs text-[#828282] mb-0.5 block">Jam Akhir</label>
                <flat-pickr
                  :value="getDayConfig(day).end || '17:00'"
                  :config="configDate"
                  class="form-control form-control-sm text-center bg-white"
                  @input="val => updateDayTime(day, 'end', val)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-card>
</template>

<script>
import flatPickr from 'vue-flatpickr-component'
import { days } from '../config'
import 'flatpickr/dist/themes/light.css'
// eslint-disable-next-line import/order
import { Indonesian } from 'flatpickr/dist/l10n/id'

export default {
  components: {
    flatPickr,
  },
  props: {
    formData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      setCsTime: false,
      selectedDays: [],
      days,
      configDate: {
        dateFormat: 'H:i',
        enableTime: true,
        noCalendar: true,
        time_24hr: true,
        allowInput: true,
        static: true,
        wrap: false,
        clickOpens: true,
        locale: Indonesian,
      },
    }
  },
  computed: {
    dayOptions() {
      return this.days.filter(d => d.name !== 'Setiap Hari')
    },
    isAllDaysSelected() {
      return this.selectedDays.length === this.dayOptions.length && this.dayOptions.length > 0
    },
  },
  watch: {
    'formData.times': {
      immediate: true,
      handler(newTimes) {
        if (!newTimes || !Array.isArray(newTimes)) return
        if (newTimes.length > 0 && !this.setCsTime) {
          this.setCsTime = true
          const converted = newTimes.map(t => {
            if (!t || !t.day_of_week) return null
            const dayObj = this.dayOptions.find(d => d.value === t.day_of_week)
            if (!dayObj) return null
            return {
              ...dayObj,
              fullDay: t.is_24_hours || (t.start_time === '00:00:00' && t.end_time === '23:59:00'),
              start: t.start_time ? t.start_time.substring(0, 5) : '08:00',
              end: t.end_time ? t.end_time.substring(0, 5) : '17:00',
            }
          }).filter(Boolean)
          if (converted.length > 0) {
            this.selectedDays = converted
          }
        }
      },
    },
    selectedDays: {
      deep: true,
      handler() {
        this.prepareSchedulesForForm()
      },
    },
    setCsTime(isSet) {
      if (!isSet) {
        this.selectedDays = []
      } else if (this.selectedDays.length === 0) {
        // Default: select all days as 24h when turned on
        this.selectedDays = this.dayOptions.map(d => ({
          ...d,
          fullDay: true,
          start: '00:00',
          end: '23:59',
        }))
      }
      this.prepareSchedulesForForm()
    },
  },
  methods: {
    isDaySelected(day) {
      return this.selectedDays.some(s => s.id === day.id)
    },
    getDayConfig(day) {
      return this.selectedDays.find(s => s.id === day.id) || {
        ...day,
        fullDay: true,
        start: '08:00',
        end: '17:00',
      }
    },
    toggleDaySelection(day, isChecked) {
      if (isChecked) {
        if (!this.isDaySelected(day)) {
          this.selectedDays.push({
            ...day,
            fullDay: true,
            start: '00:00',
            end: '23:59',
          })
        }
      } else {
        this.selectedDays = this.selectedDays.filter(s => s.id !== day.id)
      }
    },
    toggleSelectAllDays(isChecked) {
      if (isChecked) {
        this.selectedDays = this.dayOptions.map(d => ({
          ...d,
          fullDay: true,
          start: '00:00',
          end: '23:59',
        }))
      } else {
        this.selectedDays = []
      }
    },
    toggleFullDay(day) {
      const target = this.selectedDays.find(s => s.id === day.id)
      if (target) {
        target.fullDay = !target.fullDay
        if (target.fullDay) {
          target.start = '00:00'
          target.end = '23:59'
        } else if (!target.start || target.start === '00:00') {
          target.start = '08:00'
          target.end = '17:00'
        }
      }
    },
    updateDayTime(day, type, value) {
      const target = this.selectedDays.find(s => s.id === day.id)
      if (target) {
        target[type] = value
      }
    },
    prepareSchedulesForForm() {
      if (!this.setCsTime || !Array.isArray(this.selectedDays)) {
        this.formData.times = []
        return
      }

      this.formData.times = this.selectedDays
        .filter(d => d && d.value)
        .map(d => ({
          day_of_week: d.value,
          is_24_hours: !!d.fullDay,
          start_time: d.fullDay ? '00:00' : (d.start || '00:00'),
          end_time: d.fullDay ? '23:59' : (d.end || '23:59'),
        }))
    },
  },
}
</script>

<style lang="scss">
.custom-cs-switch {
  .custom-control-input:checked ~ .custom-control-label::before {
    border-color: #F97316 !important;
    background-color: #F97316 !important;
  }
}

.custom-cs-checkbox {
  .custom-control-label::before {
    border-radius: 4px !important;
    border: 1.5px solid #CBD5E1 !important;
  }
  .custom-control-input:checked ~ .custom-control-label::before {
    border-color: #F97316 !important;
    background-color: #F97316 !important;
  }
}
</style>
